import React from 'react';
import { observer } from 'mobx-react-lite';
import { Divider, Grid, Link, MenuItem, TextField, Typography } from '@material-ui/core';
import { IEmergencyContact, ITenantProfile, IUserBio } from 'realhaus-sdk';
import { InputField } from '../formComponents/inputfield';
import { GridContent, GridMainContent, GridSideContent } from '../uiComponents/UIComponents';
import { DatePicker } from '../datePicker/datepicker';
import { useStyles } from './styles';
import { InputFieldPhone } from '../formComponents/inputfieldPhone';
import { paths } from '../../routes';
import { Button, Stack } from '@mui/material';
import { IsValidNumber, StringIsNullOrWhitespace } from '../../global/util';
import { PersonaModal } from '../identityVerification/personaModal';

interface BioInfoProps {
    profile: ITenantProfile;
    userBio: IUserBio;
    updateProfile: (
        prop: keyof ITenantProfile,
    ) => (value: string | number | IEmergencyContact | null) => void;
    updateUserBio: (prop: keyof IUserBio) => (value: string | number | null) => void;
    userProfileVerified: boolean;
}

export const BioInfoComponent: React.FC<BioInfoProps> = observer(
    ({ profile, userBio, updateProfile, updateUserBio, userProfileVerified }) => {
        const classes = useStyles();

        const [openVerificationModal, setOpenVerificationModal] = React.useState<boolean>(false);
        const [docViewerOpen, setDocViewerOpen] = React.useState<boolean>(false);

        const handleDOBChange = (date: Date | null) => {
            updateUserBio('dateOfBirth')(!!date ? date.getTime() : 0);
        };

        const handleCloseVerificationModal = () => {
            setOpenVerificationModal(false);
        };

        const handleIDVerificationComplete = () => {
            handleCloseVerificationModal();
        };

        const handleChange =
            (prop: keyof IUserBio) => (event: React.ChangeEvent<{ value: unknown }>) => {
                updateUserBio(prop)(event.target.value as string);
            };

        const handleEmergencyContactChange =
            (prop: keyof IEmergencyContact) => (value: string | number | null) => {
                updateProfile('emergencyContact')({
                    ...profile.emergencyContact,
                    [prop]: value?.toString(),
                });
            };

        const handleVerifyIdentity = () => {
            setOpenVerificationModal(true);
        };

        const verifyIdentityButtonDisabled =
            StringIsNullOrWhitespace(userBio.firstname) ||
            StringIsNullOrWhitespace(userBio.lastname) ||
            StringIsNullOrWhitespace(userBio.gender) ||
            !IsValidNumber(userBio.dateOfBirth) ||
            !profile.emergencyContact ||
            StringIsNullOrWhitespace(profile.emergencyContact.name) ||
            StringIsNullOrWhitespace(profile.emergencyContact.email) ||
            StringIsNullOrWhitespace(profile.emergencyContact.phone);

        const handleDocViewerOpen = () => {
            setDocViewerOpen(true);
        };

        const handleDocViewerClose = () => {
            setDocViewerOpen(false);
        };

        return (
            <>
                <GridContent spacing={5}>
                    <GridMainContent>
                        <Typography variant='h5' gutterBottom>
                            Tell the landlord about yourself
                        </Typography>
                    </GridMainContent>
                    <GridMainContent withSidebar>
                        <Grid container spacing={2} className={classes.grid}>
                            {/* First Name */}
                            <Grid item xs={6}>
                                <Typography variant='subtitle2' gutterBottom>
                                    FIRST NAME
                                </Typography>
                                <InputField
                                    id='firstname'
                                    fullWidth
                                    placeholder='First Name'
                                    value={userBio?.firstname ?? ''}
                                    onChange={updateUserBio('firstname')}
                                    disable={userProfileVerified}
                                />
                            </Grid>
                            {/* Last Name */}
                            <Grid item xs={6}>
                                <Typography variant='subtitle2' gutterBottom>
                                    LAST NAME
                                </Typography>
                                <InputField
                                    id='lastname'
                                    fullWidth
                                    placeholder='Last Name'
                                    value={userBio?.lastname ?? ''}
                                    onChange={updateUserBio('lastname')}
                                    disable={userProfileVerified}
                                />
                            </Grid>
                            {/* Date of Birth */}
                            <Grid item xs={6}>
                                <Typography variant='subtitle2' gutterBottom>
                                    DATE OF BIRTH
                                </Typography>
                                <DatePicker
                                    id='dob-picker'
                                    selectedDate={
                                        userBio.dateOfBirth ? new Date(userBio.dateOfBirth) : null
                                    }
                                    onChange={handleDOBChange}
                                    disable={userProfileVerified}
                                />
                            </Grid>
                            {/* Gender */}
                            <Grid item xs={6}>
                                <Typography variant='subtitle2' gutterBottom>
                                    Gender
                                </Typography>
                                <TextField
                                    select
                                    margin='normal'
                                    id='gender'
                                    variant='outlined'
                                    value={userBio.gender ?? ''}
                                    onChange={handleChange('gender')}
                                    fullWidth
                                    disabled={userProfileVerified}
                                >
                                    <MenuItem value='male'>Male</MenuItem>
                                    <MenuItem value='female'>Female</MenuItem>
                                    <MenuItem value='other'>Other</MenuItem>
                                </TextField>
                            </Grid>
                            <Divider variant='middle' className={classes.divider} />
                            {!userProfileVerified && !verifyIdentityButtonDisabled && (
                                <Grid item xs={12}>
                                    <Stack
                                        direction='row'
                                        spacing={3}
                                        sx={{
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                        }}
                                    >
                                        <Typography variant='subtitle1' color='error'>
                                            Your identity has not been verified
                                        </Typography>
                                        <Button
                                            variant='outlined'
                                            color='info'
                                            onClick={handleVerifyIdentity}
                                            disabled={verifyIdentityButtonDisabled}
                                        >
                                            Begin Verification
                                        </Button>
                                    </Stack>
                                </Grid>
                            )}
                            <Divider variant='middle' className={classes.divider} />
                            <Grid item xs={12}>
                                <Typography variant='h6' gutterBottom>
                                    Emergency Contact
                                </Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                        <Typography variant='subtitle2' gutterBottom>
                                            Full Name
                                        </Typography>
                                        <InputField
                                            id='fullname'
                                            fullWidth
                                            placeholder='Full Name'
                                            value={profile?.emergencyContact?.name ?? ''}
                                            onChange={handleEmergencyContactChange('name')}
                                            disable={userProfileVerified}
                                        />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant='subtitle2' gutterBottom>
                                            Email
                                        </Typography>
                                        <InputField
                                            id='email'
                                            fullWidth
                                            placeholder="Contact's Email"
                                            value={profile?.emergencyContact?.email ?? ''}
                                            onChange={handleEmergencyContactChange('email')}
                                            disable={userProfileVerified}
                                        />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant='subtitle2' gutterBottom>
                                            Phone Number
                                        </Typography>
                                        <InputFieldPhone
                                            id='emergencyPhone'
                                            fullWidth
                                            value={profile?.emergencyContact?.phone ?? ''}
                                            onChange={handleEmergencyContactChange('phone')}
                                            disable={userProfileVerified}
                                        />
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    </GridMainContent>
                    <GridSideContent>
                        <Typography variant='body1' align='left'>
                            Make sure all the fields in the application are filled as accurate as
                            possible so the landlord has all the information they need to make a
                            decision.
                            <br />
                            <br />
                            Your personal information can be updated through the{' '}
                            <Link color='primary' href={paths.account.personalInfo}>
                                personal info
                            </Link>{' '}
                            page.
                            <br />
                            <br />
                            Your emergency contact information can be updated through the{' '}
                            <Link color='primary' href={paths.tenant.profile}>
                                profile page
                            </Link>
                            .
                            <br />
                            <br />
                            Your paystub or employment letter information can be updated through the{' '}
                            <Link color='primary' href={paths.tenant.profile}>
                                profile page
                            </Link>
                            , occupation section.
                        </Typography>
                        <Typography variant='body1' align='justify'></Typography>
                    </GridSideContent>
                </GridContent>
                {/* Verification Modal- Persona */}
                <PersonaModal
                    person={{
                        firstname: userBio.firstname,
                        lastname: userBio.lastname,
                        dob: {
                            year: new Date(userBio.dateOfBirth).getFullYear(),
                            month: new Date(userBio.dateOfBirth).getMonth() + 1,
                            day: new Date(userBio.dateOfBirth).getDate(),
                        },
                    }}
                    closeVerificationModal={handleCloseVerificationModal}
                    openVerificationModal={openVerificationModal}
                    onVerificationComplete={handleIDVerificationComplete}
                />
            </>
        );
    },
);
